<template>
  <div>
    <v-container fluid>
      <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
        <v-toolbar dark class="header-title text-white">
          <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">System Configuration</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>
      <v-card class="card2" style="margin-top: 1rem">
        <v-card-title class="py-4 px-4">
          <v-row align="center" no-gutters>
            <!-- Title Section -->
            <v-col cols="12" sm="12" md="4" lg="4" xl="4" class="d-flex align-center pb-3 pb-md-0">
              <v-icon color="blue-grey darken-2 mr-5" size="28">mdi-cog</v-icon>
              <div>
                <h3 class="text-h6 mb-1">System Configuration</h3>
                <p class="text-caption text-grey-darken-1 mb-0">Configure system settings and language preferences</p>
              </div>
            </v-col>

            <!-- Controls Section -->
            <v-col cols="12" sm="12" md="8" lg="8" xl="8">
              <v-row align="center" justify="end" no-gutters>
                <!-- Space for future controls if needed -->
              </v-row>
            </v-col>
          </v-row>
        </v-card-title>

        <v-card-text>
          <v-tabs v-model="activeTab" show-arrows>
            <v-tab value="languages">Language Settings</v-tab>
            <v-tab value="voice">Voice Feature</v-tab>
            <v-tab value="general">General Settings</v-tab>
          </v-tabs>

          <v-tabs-window v-model="activeTab">
            <!-- Language Settings Tab -->
            <v-tabs-window-item value="languages">
              <v-container>
                <v-row class="mb-4">
                  <v-col cols="12">
                    <h3 class="mb-3">Language Configuration</h3>
                    <p class="text-subtitle-1 mb-4">
                      Manage supported languages for chatbot conversations
                    </p>
                  </v-col>
                </v-row>

                <!-- Language Selection with Checkboxes -->
                <v-row>
                  <v-col cols="12">
                    <v-card variant="outlined">
                      <v-card-title class="py-4 px-4">
                        <v-row align="center" no-gutters>
                          <!-- Title Section -->
                          <v-col cols="12" sm="12" md="6" lg="6" xl="6" class="d-flex align-center pb-3 pb-md-0">
                            <v-icon color="blue-grey darken-2 mr-5" size="28">mdi-translate</v-icon>
                            <div>
                              <h3 class="text-h6 mb-1">Language Configuration</h3>
                              <p class="text-caption text-grey-darken-1 mb-0">Select a language to interact with chatbot</p>
                            </div>
                          </v-col>

                          <!-- Controls Section -->
                          <v-col cols="12" sm="12" md="6" lg="6" xl="6">
                            <v-row align="center" justify="end" no-gutters>
                              <v-col cols="12" sm="5" md="5" lg="5" xl="5">
                                <v-text-field
                                  v-model="languageSearch"
                                  prepend-inner-icon="mdi-magnify"
                                  placeholder="Search languages"
                                  hide-details
                                  variant="outlined"
                                  rounded
                                  class="search-field"
                                ></v-text-field>
                              </v-col>

                              <v-col cols="auto" class="ml-2 ml-sm-3">
                                <!-- Add Language Button -->
                                <v-btn
                                  color="primary"
                                  dark
                                  class="btn-layout"
                                  @click="openAddLanguageDialog"
                                >
                                  <v-icon left>mdi-plus</v-icon>
                                  Add Language
                                </v-btn>
                              </v-col>
                            </v-row>
                          </v-col>
                        </v-row>
                      </v-card-title>
                      <v-card-text>
                        <div v-if="loadingLanguages" class="text-center py-8">
                          <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
                          <p class="mt-3 text-body-1">Loading languages...</p>
                        </div>
                        
                        <div v-else-if="filteredLanguages.length === 0" class="text-center py-8">
                          <v-icon size="64" color="grey-lighten-1">mdi-translate-off</v-icon>
                          <p class="mt-3 text-body-1 text-grey">
                            {{ languageSearch ? 'No languages found matching your search' : 'No languages configured yet' }}
                          </p>
                        </div>
                        
                        <div v-else>
                          <!-- Language Checkboxes Grid -->
                          <v-row>
                            <v-col 
                              v-for="language in filteredLanguages" 
                              :key="language.id"
                              cols="12" 
                              sm="12" 
                              md="12"
                              class="py-2"
                            >
                              <div class="language-checkbox-item">
                                <v-card 
                                  :class="['language-card', { 'language-card--active': language.isActive }]"
                                  variant="outlined"
                                  :ripple="false"
                                >
                                  <v-card-text class="pa-3">
                                    <div class="d-flex align-center">
                                      <v-checkbox
                                        v-model="language.isActive"
                                        @update:model-value="toggleLanguageStatus(language)"
                                        :disabled="togglingStatus === language.id"
                                        color="primary"
                                        class="mt-0 pt-0 checkbox-custom"
                                        hide-details
                                      ></v-checkbox>
                                      
                                      <div class="ml-2 flex-grow-1">
                                        <div class="d-flex align-center mb-1">
                                          <span class="text-body-1 font-weight-medium">
                                            {{ language.languageName }}
                                          </span>
                                          <v-chip 
                                            v-if="language.isDefault" 
                                            size="small" 
                                            color="primary" 
                                            text-color="white"
                                            class="ml-2"
                                          >
                                            <v-icon start size="x-small">mdi-star</v-icon>
                                            Default
                                          </v-chip>
                                        </div>
                                      </div>
                                      
                                      <v-menu offset-y location="start">
                                        <template v-slot:activator="{ props }">
                                          <v-btn
                                            icon
                                            v-bind="props"
                                            variant="text"
                                            density="comfortable"
                                            class="ml-1"
                                          >
                                            <v-icon size="small">mdi-dots-vertical</v-icon>
                                          </v-btn>
                                        </template>
                                        <v-list density="compact">
                                          <v-list-item 
                                            @click="setDefaultLanguage(language.languageCode)"
                                            :disabled="language.isDefault || settingDefault === language.languageCode"
                                          >
                                            <template v-slot:prepend>
                                              <v-icon size="small">mdi-star</v-icon>
                                            </template>
                                            <v-list-item-title>Set as Default</v-list-item-title>
                                          </v-list-item>
                                          <v-list-item @click="editLanguage(language)">
                                            <template v-slot:prepend>
                                              <v-icon size="small">mdi-pencil</v-icon>
                                            </template>
                                            <v-list-item-title>Edit</v-list-item-title>
                                          </v-list-item>
                                          <v-list-item 
                                            @click="confirmDeleteLanguage(language)"
                                            :disabled="language.isDefault"
                                          >
                                            <template v-slot:prepend>
                                              <v-icon size="small" color="red">mdi-delete</v-icon>
                                            </template>
                                            <v-list-item-title class="text-red">Delete</v-list-item-title>
                                          </v-list-item>
                                        </v-list>
                                      </v-menu>
                                    </div>
                                  </v-card-text>
                                </v-card>
                              </div>
                            </v-col>
                          </v-row>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-container>
            </v-tabs-window-item>

            <!-- Voice Feature Tab -->
            <v-tabs-window-item value="voice">
              <v-container>
                <v-row class="mb-4">
                  <v-col cols="12">
                    <h3 class="mb-3">Voice Feature Configuration</h3>
                    <p class="text-subtitle-1 mb-4">
                      Enable or disable voice chat functionality for the chatbot
                    </p>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-card variant="outlined">
                      <v-card-title>
                        <v-icon start color="primary">mdi-microphone</v-icon>
                        Voice Chat Feature
                      </v-card-title>
                      <v-card-text>
                        <v-row align="center">
                          <v-col cols="auto">
                            <v-switch
                              v-model="voiceFeatureEnabled"
                              @update:model-value="toggleVoiceFeature"
                              :loading="updatingVoiceFeature"
                              color="primary"
                              label="Enable Voice Chat"
                            ></v-switch>
                          </v-col>
                          <v-col>
                            <p class="mb-0">
                              {{ voiceFeatureEnabled ? 'Voice chat is enabled' : 'Voice chat is disabled' }}
                            </p>
                            <p class="text-caption mb-0">
                              {{ voiceFeatureEnabled 
                                  ? 'Users can interact with the chatbot using voice messages' 
                                  : 'Users can only interact with text messages' 
                              }}
                            </p>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  
                  <v-col cols="12" md="6">
                    <v-card variant="outlined">
                      <v-card-title>
                        <v-icon start color="info">mdi-information</v-icon>
                        Voice Feature Info
                      </v-card-title>
                      <v-card-text>
                        <v-list density="compact">
                          <v-list-item>
                            <template v-slot:prepend>
                              <v-icon color="success">mdi-check</v-icon>
                            </template>
                            <v-list-item-title>Speech-to-Text</v-list-item-title>
                            <v-list-item-subtitle>Convert voice to text messages</v-list-item-subtitle>
                          </v-list-item>
                          <v-list-item>
                            <template v-slot:prepend>
                              <v-icon color="success">mdi-check</v-icon>
                            </template>
                            <v-list-item-title>Text-to-Speech</v-list-item-title>
                            <v-list-item-subtitle>Convert bot responses to voice</v-list-item-subtitle>
                          </v-list-item>
                          <v-list-item>
                            <template v-slot:prepend>
                              <v-icon color="warning">mdi-alert</v-icon>
                            </template>
                            <v-list-item-title>Language Support</v-list-item-title>
                            <v-list-item-subtitle>Voice feature supports configured languages</v-list-item-subtitle>
                          </v-list-item>
                        </v-list>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-container>
            </v-tabs-window-item>

            <!-- General Settings Tab -->
            <v-tabs-window-item value="general">
              <v-container>
                <v-row class="mb-4">
                  <v-col cols="12">
                    <h3 class="mb-3">General System Settings</h3>
                    <p class="text-subtitle-1 mb-4">
                      Configure general system parameters and features
                    </p>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <v-card variant="outlined">
                      <v-card-title class="py-4 px-4">
                        <v-row align="center" no-gutters>
                          <v-col cols="12" sm="12" md="6" lg="6" xl="6" class="d-flex align-center pb-3 pb-md-0">
                            <v-icon color="blue-grey darken-2 mr-5" size="28">mdi-cog</v-icon>
                            <div>
                              <h3 class="text-h6 mb-1">System Configurations</h3>
                              <p class="text-caption text-grey-darken-1 mb-0">Manage system parameters and settings</p>
                            </div>
                          </v-col>
                          <v-col cols="12" sm="12" md="6" lg="6" xl="6">
                            <v-row align="center" justify="end" no-gutters>
                              <v-col cols="auto" class="ml-2">
                                <v-btn
                                  color="primary"
                                  dark
                                  class="btn-layout mr-2"
                                  @click="openAddConfigDialog"
                                >
                                  <v-icon left>mdi-plus</v-icon>
                                  Add Config
                                </v-btn>
                                <v-btn
                                  class="btn-export"
                                  outlined
                                  @click="refreshConfigs"
                                  :loading="loadingConfigs"
                                >
                                  <v-icon left>mdi-refresh</v-icon>
                                  Refresh
                                </v-btn>
                              </v-col>
                            </v-row>
                          </v-col>
                        </v-row>
                      </v-card-title>
                      <v-card-item>
                        <div class="table-container">
                          <v-data-table
                            :headers="configHeaders"
                            :items="systemConfigs"
                            :loading="loadingConfigs"
                            class="my-data-table"
                            fixed-header
                            :items-per-page="10"
                          >
                            <template v-slot:headers>
                              <tr>
                                <th style="text-align: left; background-color: #f5f5f5">Display Name</th>
                                <th style="text-align: center; background-color: #f5f5f5; width: 50px;">Category</th>
                                <th style="text-align: center; background-color: #f5f5f5">Value</th>
                                <th style="text-align: center; background-color: #f5f5f5">Type</th>
                                <th style="text-align: center; background-color: #f5f5f5">Status</th>
                                <th style="text-align: center; background-color: #f5f5f5; width: 100px;">Actions</th>
                              </tr>
                            </template>
                            <template v-slot:item="{ item }">
                              <tr>
                                <td style="text-align: left">{{ item.displayName }}</td>
                                <td style="text-align: center">
                                  <v-chip
                                    :color="getCategoryColor(item.category)"
                                    text-color="white"
                                    size="small"
                                  >
                                    {{ item.category }}
                                  </v-chip>
                                </td>
                                <td style="text-align: center">
                                  <span v-if="!editingConfig || editingConfig.id !== item.id">
                                    {{ formatConfigValue(item) }}
                                  </span>
                                  <v-text-field
                                    v-else
                                    v-model="editingConfig.configValue"
                                    density="compact"
                                    hide-details
                                    variant="outlined"
                                    @keyup.enter="saveConfig"
                                    @keyup.esc="cancelEditConfig"
                                    class="ma-0"
                                    style="min-width: 150px"
                                  ></v-text-field>
                                </td>
                                <td style="text-align: center">{{ item.configType }}</td>
                                <td style="text-align: center">
                                  <v-chip
                                    :color="item.isActive ? 'success' : 'error'"
                                    text-color="white"
                                    size="small"
                                  >
                                    {{ item.isActive ? 'Active' : 'Inactive' }}
                                  </v-chip>
                                </td>
                                <td style="text-align: center">
                                  <div class="action-group" style="justify-content: center; display: flex">
                                    <v-btn
                                      v-if="item.isEditable && (!editingConfig || editingConfig.id !== item.id)"
                                      variant="text"
                                      density="comfortable"
                                      @click="startEditConfig(item)"
                                      class="action-btn edit-btn"
                                      icon
                                    >
                                      <v-icon size="18">mdi-pencil-outline</v-icon>
                                      <v-tooltip activator="parent" location="top">Edit</v-tooltip>
                                    </v-btn>
                                    <v-btn
                                      v-else-if="!item.isEditable && (!editingConfig || editingConfig.id !== item.id)"
                                      variant="text"
                                      density="comfortable"
                                      disabled
                                      class="action-btn"
                                      icon
                                    >
                                      <v-icon size="18" color="grey-lighten-1">mdi-lock</v-icon>
                                      <v-tooltip activator="parent" location="top">Read Only</v-tooltip>
                                    </v-btn>
                                    <template v-else-if="editingConfig && editingConfig.id === item.id">
                                      <v-btn
                                        variant="text"
                                        density="comfortable"
                                        @click="saveConfig"
                                        :loading="savingConfig"
                                        class="action-btn save-btn"
                                        icon
                                        color="success"
                                      >
                                        <v-icon size="18">mdi-check</v-icon>
                                        <v-tooltip activator="parent" location="top">Save</v-tooltip>
                                      </v-btn>
                                      <v-btn
                                        variant="text"
                                        density="comfortable"
                                        @click="cancelEditConfig"
                                        class="action-btn cancel-btn"
                                        icon
                                        color="error"
                                      >
                                        <v-icon size="18">mdi-close</v-icon>
                                        <v-tooltip activator="parent" location="top">Cancel</v-tooltip>
                                      </v-btn>
                                    </template>
                                  </div>
                                </td>
                              </tr>
                            </template>
                          </v-data-table>
                        </div>
                      </v-card-item>
                    </v-card>
                  </v-col>
                </v-row>
              </v-container>
            </v-tabs-window-item>
          </v-tabs-window>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Add Language Dialog -->
    <v-dialog v-model="addLanguageDialog" max-width="800px" persistent>
      <v-card class="add-language-dialog">
        <!-- Header Section -->
        <div class="dialog-header dialog-header-add">
          <div class="header-main">
            <div class="header-icon-container">
              <div class="icon-background">
                <v-icon color="#135EB8" size="28">mdi-plus</v-icon>
              </div>
            </div>
            <div class="header-text-content">
              <h2 class="dialog-title">Add New Language</h2>
              <p class="dialog-subtitle">Create a new language configuration</p>
            </div>
          </div>
          <div class="header-actions">
            <v-btn icon variant="text" @click="closeAddLanguageDialog" class="close-button" color="#1F1F1F">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <v-divider></v-divider>

        <v-card-text class="dialog-content">
          <v-form ref="addLanguageForm" v-model="formValid">
            <!-- Language Information Section -->
            <div class="form-section">
              <h4 class="section-title">Language Information</h4>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newLanguage.languageCode"
                    label="Language Code *"
                    placeholder="en, th, jp"
                    hint="ISO 639-1 language code"
                    persistent-hint
                    :rules="languageCodeRules"
                    maxlength="10"
                    variant="outlined"
                    rounded
                    class="custom-rounded"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newLanguage.languageName"
                    label="Language Name *"
                    placeholder="English, Thai, Japanese"
                    :rules="requiredRules"
                    maxlength="100"
                    variant="outlined"
                    rounded
                    class="custom-rounded"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newLanguage.nativeName"
                    label="Native Name *"
                    placeholder="English, ไทย, 日本語"
                    :rules="requiredRules"
                    maxlength="100"
                    variant="outlined"
                    rounded
                    class="custom-rounded"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newLanguage.countryCode"
                    label="Country Code"
                    placeholder="US, TH, JP"
                    maxlength="10"
                    variant="outlined"
                    rounded
                    class="custom-rounded"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>

        <!-- Dialog Actions -->
        <div class="dialog-actions">
          <div class="action-buttons">           
            <v-btn
              variant="outlined"
              @click="closeAddLanguageDialog"
              class="btn-close"
            >
              <v-icon>mdi-close</v-icon> Cancel
            </v-btn>
            <v-btn
            color="primary"
              @click="addLanguage"
              :loading="addingLanguage"
              :disabled="!isNewLanguageValid"
              class="btn-save"
            >
              Save
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- Edit Language Dialog -->
    <v-dialog v-model="editLanguageDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Edit Language</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editingLanguageData.languageCode"
                  label="Language Code *"
                  :rules="languageCodeRules"
                  maxlength="10"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editingLanguageData.languageName"
                  label="Language Name *"
                  :rules="requiredRules"
                  maxlength="100"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editingLanguageData.nativeName"
                  label="Native Name *"
                  :rules="requiredRules"
                  maxlength="100"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editingLanguageData.countryCode"
                  label="Country Code"
                  maxlength="10"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="editingLanguageData.description"
                  label="Description"
                  rows="3"
                  maxlength="500"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" variant="text" @click="closeEditLanguageDialog">Cancel</v-btn>
          <v-btn
            color="blue-darken-1"
            variant="text"
            @click="updateLanguage"
            :loading="updatingLanguage"
            :disabled="!isEditLanguageValid"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Language Confirmation Dialog -->
    <v-dialog v-model="deleteLanguageDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h5">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete the language "{{ languageToDelete?.languageName }}"?
          <br><br>
          <v-alert type="warning" density="compact">
            This action cannot be undone. The language will be removed from the system.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" variant="text" @click="deleteLanguageDialog = false">Cancel</v-btn>
          <v-btn
            color="red-darken-1"
            variant="text"
            @click="deleteLanguage"
            :loading="deletingLanguage"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Config Dialog -->
    <v-dialog v-model="addConfigDialog" max-width="800px" persistent>
      <v-card class="add-config-dialog">
        <!-- Header Section -->
        <div class="dialog-header dialog-header-add">
          <div class="header-main">
            <div class="header-icon-container">
              <div class="icon-background">
                <v-icon color="#135EB8" size="28">mdi-plus</v-icon>
              </div>
            </div>
            <div class="header-text-content">
              <h2 class="dialog-title">Add New Configuration</h2>
              <p class="dialog-subtitle">Create a new system configuration</p>
            </div>
          </div>
          <div class="header-actions">
            <v-btn icon variant="text" @click="closeAddConfigDialog" class="close-button" color="#1F1F1F">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <v-divider></v-divider>

        <v-card-text class="dialog-content">
          <v-form ref="addConfigForm" v-model="configFormValid">
            <!-- Configuration Information Section -->
            <div class="form-section">
              <h4 class="section-title">Configuration Information</h4>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newConfig.configKey"
                    label="Configuration Key *"
                    placeholder="FEATURE_VOICE_ENABLED"
                    hint="Unique identifier for the configuration"
                    persistent-hint
                    :rules="configKeyRules"
                    maxlength="100"
                    variant="outlined"
                    rounded
                    class="custom-rounded"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newConfig.displayName"
                    label="Display Name *"
                    placeholder="Voice Feature"
                    hint="Human-readable name for display"
                    persistent-hint
                    :rules="requiredRules"
                    maxlength="200"
                    variant="outlined"
                    rounded
                    class="custom-rounded"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="newConfig.category"
                    label="Category *"
                    :items="configCategories"
                    :rules="requiredRules"
                    variant="outlined"
                    rounded
                    class="custom-rounded"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="newConfig.configType"
                    label="Configuration Type *"
                    :items="configTypes"
                    :rules="requiredRules"
                    variant="outlined"
                    rounded
                    class="custom-rounded"
                  ></v-select>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="newConfig.configValue"
                    label="Configuration Value *"
                    placeholder="true, false, or custom value"
                    :rules="requiredRules"
                    variant="outlined"
                    rounded
                    class="custom-rounded"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="newConfig.description"
                    label="Description"
                    placeholder="Brief description of this configuration"
                    rows="3"
                    variant="outlined"
                    rounded
                    class="custom-rounded"
                  ></v-textarea>
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>

        <!-- Dialog Actions -->
        <div class="dialog-actions">
          <div class="action-buttons">           
            <v-btn
              variant="outlined"
              @click="closeAddConfigDialog"
              class="btn-close"
            >
              <v-icon>mdi-close</v-icon> Cancel
            </v-btn>
            <v-btn
              color="primary"
              @click="addConfig"
              :loading="addingConfig"
              :disabled="!isNewConfigValid"
              class="btn-save"
            >
              Save
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- Success/Error Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: 'SystemConfiguration',
  inject: ['flagNav', 'FontColorTitle'],
  components: {},
  data: () => ({
    // Current tab
    activeTab: null,
    today: null,

    // Loading states
    loadingLanguages: false,
    loadingConfigs: false,
    addingLanguage: false,
    updatingLanguage: false,
    deletingLanguage: false,
    updatingVoiceFeature: false,
    savingConfig: false,
    settingDefault: null,
    togglingStatus: null,
    editingLanguage: null,
    addingConfig: false,

    // Language management
    languages: [],
    newLanguage: {
      languageCode: '',
      languageName: '',
      nativeName: '',
      countryCode: '',
      description: ''
    },
    languageSearch: '',
    addLanguageDialog: false,
    editLanguageDialog: false,
    deleteLanguageDialog: false,
    editingLanguageData: {},
    languageToDelete: null,

    // Voice feature
    voiceFeatureEnabled: false,

    // System configs
    systemConfigs: [],
    editingConfig: null,
    newConfig: {
      configKey: '',
      category: '',
      configValue: '',
      description: '',
      displayName: '',
      configType: 'String'
    },
    addConfigDialog: false,

    // Configuration categories
    configCategories: [
      'System',
      'Features',
      'General',
      'Notification',
      'Security',
      'Limits',
      'Other'
    ],

    // Configuration types
    configTypes: [
      'String',
      'Integer',
      'Boolean',
      'Decimal',
      'Json',
      'Other'
    ],

    // Form validation
    formValid: false,
    configFormValid: false,
    submittingConfig: false,
    
    // Loading states
    loading: false,
    loadingLanguages: false,
    
    // Snackbar
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'info',

    // Snackbar
    snackbar: {
      show: false,
      message: '',
      color: 'success',
      timeout: 3000
    },

    // Table headers
    languageHeaders: [
      { text: 'Code', value: 'languageCode', sortable: true },
      { text: 'Language Name', value: 'languageName', sortable: true },
      { text: 'Native Name', value: 'nativeName', sortable: true },
      { text: 'Country', value: 'countryCode', sortable: true },
      { text: 'Default', value: 'isDefault', sortable: true, align: 'center' },
      { text: 'Active', value: 'isActive', sortable: true, align: 'center' },
      { text: 'Actions', value: 'actions', sortable: false, align: 'center' }
    ],

    configHeaders: [
      { title: 'Display Name', key: 'displayName', sortable: true },
      { title: 'Category', key: 'category', sortable: true },
      { title: 'Value', key: 'configValue', sortable: false },
      { title: 'Type', key: 'configType', sortable: true },
      { title: 'Status', key: 'isActive', sortable: true, align: 'center' },
      { title: 'Actions', key: 'actions', sortable: false, align: 'center' }
    ],

    // Form validation rules
    requiredRules: [
      v => !!v || 'This field is required'
    ],
    languageCodeRules: [
      v => !!v || 'Language code is required',
      v => (v && v.length <= 10) || 'Language code must be less than 10 characters',
      v => (v && /^[a-z]{2,3}(-[A-Z]{2})?$/.test(v)) || 'Invalid language code format (e.g., en, th, en-US)'
    ],
    configKeyRules: [
      v => !!v || 'Configuration key is required',
      v => (v && v.length <= 100) || 'Configuration key must be less than 100 characters',
      v => (v && /^[A-Z_]+$/.test(v)) || 'Invalid configuration key format (e.g., FEATURE_VOICE_ENABLED)'
    ]
  }),
  computed: {
    filteredLanguages() {
      if (!this.languageSearch) {
        return this.languages
      }
      const search = this.languageSearch.toLowerCase()
      return this.languages.filter(lang => 
        lang.languageCode.toLowerCase().includes(search) ||
        lang.languageName.toLowerCase().includes(search) ||
        lang.nativeName.toLowerCase().includes(search)
      )
    },

    isNewLanguageValid() {
      return this.newLanguage.languageCode && 
             this.newLanguage.languageName && 
             this.newLanguage.nativeName &&
             this.languageCodeRules.every(rule => rule(this.newLanguage.languageCode) === true)
    },

    isEditLanguageValid() {
      return this.editingLanguageData.languageCode && 
             this.editingLanguageData.languageName && 
             this.editingLanguageData.nativeName &&
             this.languageCodeRules.every(rule => rule(this.editingLanguageData.languageCode) === true)
    },

    isNewConfigValid() {
      return this.newConfig.configKey && 
             this.newConfig.category && 
             this.newConfig.configValue &&
             this.newConfig.displayName &&
             this.newConfig.configType &&
             this.configKeyRules.every(rule => rule(this.newConfig.configKey) === true)
    }
  },
  mounted() {
    this.getDate()
    this.loadSystemConfiguration()
  },
  methods: {
    // Data loading methods
    loadSystemConfiguration() {
      try {
        this.loadingLanguages = true
        this.loadingConfigs = true
        
        // Load system configuration overview using $call
        this.$call.GetSystemConfigOverview(
          (dataResponse) => {
            this.loadingConfigs = false
            if (dataResponse.status === 0) {
              
              // Extract languages
              if (dataResponse.data.languages) {
                this.languages = dataResponse.data.languages
              }
              
              // Extract voice feature status
              if (dataResponse.data.voiceFeatureEnabled !== undefined) {
                this.voiceFeatureEnabled = dataResponse.data.voiceFeatureEnabled
              }
              
              // Load system configs separately
              if(dataResponse.data.systemConfigs) {
                this.systemConfigs = dataResponse.data.systemConfigs
              }
              else
              {
                this.loadSystemConfigs()
              }
              
              this.showSnackbar('System configuration loaded successfully', 'success')
            } else {
              this.showSnackbar(dataResponse.message || 'Error loading system configuration', 'error')
            }
            this.loadingLanguages = false
          },
          (errorResource) => {
            this.showSnackbar('Error loading system configuration', 'error')
            this.loadingLanguages = false
            this.loadingConfigs = false
          }
        )
      } catch (error) {
        this.showSnackbar('Error loading system configuration', 'error')
        this.loadingLanguages = false
        this.loadingConfigs = false
      }
    },

    // Get current date
    getDate() {
      const todayDate = new Date()
      const dd = String(todayDate.getDate()).padStart(2, '0')
      const mm = String(todayDate.getMonth() + 1).padStart(2, '0')
      const yyyy = todayDate.getFullYear()
      this.today = `${dd}/${mm}/${yyyy}`
    },

    loadSystemConfigs() {
      try {
        this.loadingConfigs = true
        
        this.$call.GetSystemConfigs(
          (dataResponse) => {
            if (dataResponse.status === 0) {
              this.systemConfigs = dataResponse.data || []
            } else {
              this.showSnackbar(dataResponse.message || 'Error loading configurations', 'error')
            }
            this.loadingConfigs = false
          },
          (errorResource) => {
            this.showSnackbar('Error loading configurations', 'error')
            this.loadingConfigs = false
          }
        )
      } catch (error) {
        this.showSnackbar('Error loading configurations', 'error')
        this.loadingConfigs = false
      }
    },

    // Language management methods
    addLanguage() {
      if (!this.isNewLanguageValid) {
        return
      }
      
      try {
        this.addingLanguage = true
        
        this.$call.CreateLanguageConfig(
          this.newLanguage,
          (dataResponse) => {
            if (dataResponse.status === 0) {
              this.languages.push(dataResponse.data)
              this.closeAddLanguageDialog()
              this.showSnackbar('Language added successfully', 'success')
            } else {
              this.showSnackbar(dataResponse.message || 'Error adding language', 'error')
            }
            this.addingLanguage = false
          },
          (errorResource) => {
            this.showSnackbar('Error adding language', 'error')
            this.addingLanguage = false
          }
        )
      } catch (error) {
        this.showSnackbar('Error adding language', 'error')
        this.addingLanguage = false
      }
    },

    openAddLanguageDialog() {
      this.resetNewLanguageForm()
      this.addLanguageDialog = true
    },

    closeAddLanguageDialog() {
      this.addLanguageDialog = false
      this.resetNewLanguageForm()
    },

    resetNewLanguageForm() {
      this.newLanguage = {
        languageCode: '',
        languageName: '',
        nativeName: '',
        countryCode: '',
        description: ''
      }
      if (this.$refs.addLanguageForm) {
        this.$refs.addLanguageForm.resetValidation()
      }
    },

    editLanguage(language) {
      this.editingLanguageData = { ...language }
      this.editLanguageDialog = true
    },

    closeEditLanguageDialog() {
      this.editLanguageDialog = false
      this.editingLanguageData = {}
    },

    updateLanguage() {
      if (!this.isEditLanguageValid) return
      
      try {
        this.updatingLanguage = true
        
        this.$call.UpdateLanguageConfig(
          this.editingLanguageData.id,
          this.editingLanguageData,
          (dataResponse) => {
            if (dataResponse.status === 0) {
              const index = this.languages.findIndex(l => l.id === this.editingLanguageData.id)
              if (index !== -1) {
                this.languages.splice(index, 1, dataResponse.data)
              }
              this.closeEditLanguageDialog()
              this.showSnackbar('Language updated successfully', 'success')
            } else {
              this.showSnackbar(dataResponse.message || 'Error updating language', 'error')
            }
            this.updatingLanguage = false
          },
          (errorResource) => {
            this.showSnackbar('Error updating language', 'error')
            this.updatingLanguage = false
          }
        )
      } catch (error) {
        this.showSnackbar('Error updating language', 'error')
        this.updatingLanguage = false
      }
    },

    confirmDeleteLanguage(language) {
      this.languageToDelete = language
      this.deleteLanguageDialog = true
    },

    deleteLanguage() {
      if (!this.languageToDelete) return
      
      try {
        this.deletingLanguage = true
        
        this.$call.DeleteLanguageConfig(
          this.languageToDelete.id,
          (dataResponse) => {
            if (dataResponse.status === 0) {
              this.languages = this.languages.filter(l => l.id !== this.languageToDelete.id)
              this.deleteLanguageDialog = false
              this.languageToDelete = null
              this.showSnackbar('Language deleted successfully', 'success')
            } else {
              this.showSnackbar(dataResponse.message || 'Error deleting language', 'error')
            }
            this.deletingLanguage = false
          },
          (errorResource) => {
            this.showSnackbar('Error deleting language', 'error')
            this.deletingLanguage = false
          }
        )
      } catch (error) {
        this.showSnackbar('Error deleting language', 'error')
        this.deletingLanguage = false
      }
    },

    setDefaultLanguage(languageCode) {
      try {
        this.settingDefault = languageCode
        
        this.$call.SetDefaultLanguage(
          languageCode,
          (dataResponse) => {
            if (dataResponse.status === 0) {
              // Update languages array to reflect new default
              this.languages.forEach(lang => {
                lang.isDefault = lang.languageCode === languageCode
              })
              this.showSnackbar('Default language updated successfully', 'success')
            } else {
              this.showSnackbar(dataResponse.message || 'Error setting default language', 'error')
            }
            this.settingDefault = null
          },
          (errorResource) => {
            this.showSnackbar('Error setting default language', 'error')
            this.settingDefault = null
          }
        )
      } catch (error) {
        this.showSnackbar('Error setting default language', 'error')
        this.settingDefault = null
      }
    },

    toggleLanguageStatus(language) {
      try {
        this.togglingStatus = language.id
        
        const updatedLanguage = { ...language }
        this.$call.UpdateLanguageConfig(
          language.id,
          updatedLanguage,
          (dataResponse) => {
            if (dataResponse.status === 0) {
              this.showSnackbar(`Language ${language.isActive ? 'enabled' : 'disabled'} successfully`, 'success')
            } else {
              // Revert the change if failed
              language.isActive = !language.isActive
              this.showSnackbar(dataResponse.message || 'Error updating language status', 'error')
            }
            this.togglingStatus = null
          },
          (errorResource) => {
            // Revert the change if failed
            language.isActive = !language.isActive
            this.showSnackbar('Error updating language status', 'error')
            this.togglingStatus = null
          }
        )
      } catch (error) {
        // Revert the change if failed
        language.isActive = !language.isActive
        this.showSnackbar('Error updating language status', 'error')
        this.togglingStatus = null
      }
    },

    toggleVoiceFeature() {
      try {
        this.updatingVoiceFeature = true
        
        const request = {
          enabled: this.voiceFeatureEnabled
        }
        
        this.$call.ToggleVoiceFeature(
          request,
          (dataResponse) => {
            if (dataResponse.status === 0) {
              this.showSnackbar(`Voice feature ${this.voiceFeatureEnabled ? 'enabled' : 'disabled'} successfully`, 'success')
            } else {
              // Revert the change if failed
              this.voiceFeatureEnabled = !this.voiceFeatureEnabled
              this.showSnackbar(dataResponse.message || 'Error updating voice feature', 'error')
            }
            this.updatingVoiceFeature = false
          },
          (errorResource) => {
            // Revert the change if failed
            this.voiceFeatureEnabled = !this.voiceFeatureEnabled
            this.showSnackbar('Error updating voice feature', 'error')
            this.updatingVoiceFeature = false
          }
        )
      } catch (error) {
        // Revert the change if failed
        this.voiceFeatureEnabled = !this.voiceFeatureEnabled
        this.showSnackbar('Error updating voice feature', 'error')
        this.updatingVoiceFeature = false
      }
    },

    // Config Management Functions
    openAddConfigDialog() {
      this.resetNewConfigForm()
      this.addConfigDialog = true
    },

    closeAddConfigDialog() {
      this.addConfigDialog = false
      this.resetNewConfigForm()
    },

    resetNewConfigForm() {
      this.newConfig = {
        configKey: '',
        category: '',
        configValue: '',
        description: '',
        displayName: '',
        configType: 'String'
      }
    },

    addConfig() {
      if (!this.$refs.addConfigForm.validate()) {
        return
      }

      this.addingConfig = true
      try {
        this.$call.CreateSystemConfig(
          this.newConfig,
          (dataResponse) => {
            this.addingConfig = false
            if (dataResponse.status === 0) {
              this.showSnackbar('Configuration added successfully', 'success')
              this.closeAddConfigDialog()
              this.loadSystemConfigs() // Reload configs
            } else {
              this.showSnackbar(dataResponse.message || 'Error adding configuration', 'error')
            }
          },
          (errorResource) => {
            this.addingConfig = false
            this.showSnackbar('Error adding configuration', 'error')
          }
        )
      } catch (error) {
        this.addingConfig = false
        this.showSnackbar('Error adding configuration', 'error')
      }
    },

    showSnackbar(message, color = 'success', timeout = 3000) {
      this.snackbar = {
        show: true,
        message,
        color,
        timeout
      }
    },

    // Config editing methods
    startEditConfig(config) {
      this.editingConfig = { ...config }
    },

    saveConfig() {
      try {
        this.savingConfig = true
        this.$call.UpdateSystemConfig(
          this.editingConfig.configKey,
          { configValue: this.editingConfig.configValue },
          (dataResponse) => {
            this.savingConfig = false
            if (dataResponse.status === 0) {
              this.showSnackbar('Configuration updated successfully', 'success')
              this.editingConfig = null
              this.loadSystemConfigs()
            } else {
              this.showSnackbar(dataResponse.message || 'Error updating configuration', 'error')
            }
          },
          (errorResource) => {
            this.savingConfig = false
            this.showSnackbar('Error updating configuration', 'error')
          }
        )
      } catch (error) {
        this.savingConfig = false
        this.showSnackbar('Error updating configuration', 'error')
      }
    },

    cancelEditConfig() {
      this.editingConfig = null
    },

    formatConfigValue(config) {
      if (config.configType === 'Boolean') {
        return config.configValue === 'true' ? 'Yes' : 'No'
      }
      return config.configValue
    },

    getCategoryColor(category) {
      const colors = {
        'System': 'blue',
        'Features': 'green',
        'General': 'purple',
        'Notification': 'orange',
        'Security': 'red',
        'Limits': 'teal',
        'Other': 'grey'
      }
      return colors[category] || 'grey'
    },

    refreshConfigs() {
      this.loadSystemConfigs()
    }
  }
}
</script>
<style scoped>
.card2 {
  border-radius: 10px !important;
  margin-bottom: 2rem;
  padding-top: 1rem;
}

.btn-layout {
  padding: 14px 24px;
  width: 171px;
  height: 57px;
  background: linear-gradient(90deg, #504897 0%, #4b2667 100%);
  border-radius: 16px;
}

.btn-save {
  color: #fff !important;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 24px;
  width: 120px;
  height: 45px;
  background: linear-gradient(90deg, #504897 0%, #4b2667 100%);
  border-radius: 16px;
  flex: none;
  order: 1;
  flex-grow: 0;
}

.btn-close {
  color: #9e9e9e;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  gap: 10px;
  width: 135px;
  height: 45px;
  border: 1px solid #b4b4b4;
  border-radius: 18px;
  flex: none;
  order: 0;
  flex-grow: 0;
}

.btn-filter {
  padding: 6px 8px;
  gap: 8px;
  width: 56px;
  height: 56px;
  background: #4b2869;
  border-radius: 1000px;
}

.btn-export {
  padding: 10px 32px;
  gap: 8px;
  width: 152px;
  height: 57px;
  background: rgba(24, 92, 55, 0.1);
  border: 1px solid #185c37;
  border-radius: 16px;
}

.search-field {
  border-radius: 30px !important;
  z-index: 9999 !important;
}

.filter-field {
  border-radius: 16px !important;
}

.custom-rounded :deep(.v-field) {
  border-radius: 20px !important;
}

.custom-rounded :deep(.v-field__outline) {
  border-radius: 20px !important;
}

.custom-rounded :deep(.v-field__field) {
  border-radius: 20px !important;
}

.filter-rounded :deep(.v-field) {
  border-radius: 16px !important;
}

.filter-rounded :deep(.v-field__outline) {
  border-radius: 16px !important;
}

.filter-rounded :deep(.v-field__field) {
  border-radius: 16px !important;
}

.search-field .v-input__control .v-input__slot {
  min-height: 48px !important;
  background: #f5f5f5 !important;
  box-shadow: none !important;
  border: 1px solid #e0e0e0 !important;
}

.search-field .v-input__prepend-inner {
  margin-right: 8px;
  margin-left: 4px;
}

.search-field .v-icon {
  color: #666;
}

.filter-field .v-input__control .v-input__slot {
  min-height: 48px !important;
  background: #f5f5f5 !important;
  box-shadow: none !important;
  border: 1px solid #e0e0e0 !important;
}

.filter-field .v-input__prepend-inner {
  margin-right: 8px;
  margin-left: 4px;
}

.filter-field .v-icon {
  color: #666;
}

:deep(.v-table__wrapper thead tr) {
  background-color: #f5f5f5 !important;
}

:deep(.v-table__wrapper thead th) {
  background-color: #f5f5f5 !important;
  color: #333333;
  font-size: 14px;
  font-weight: 600;
  height: 48px;
}

:deep(.v-data-table-server .v-table thead th) {
  background-color: #f5f5f5 !important;
  color: #333333;
  font-size: 14px;
  font-weight: 600;
  height: 48px;
}

:deep(.v-data-table-server .v-table thead tr th) {
  background-color: #f5f5f5 !important;
}

:deep(.v-data-table) {
  border: 1px solid #e1e1e1;
  border-radius: 24px;
  overflow: hidden;
}

:deep(.v-table) {
  border-radius: 24px;
  overflow: hidden;
}

.v-data-table {
  overflow-x: auto;
}

.table-container {
  overflow-x: auto;
  max-width: 100%;
  position: relative;
}

.sticky-action {
  position: sticky !important;
  right: 0px !important;
  background-color: #fff !important;
  z-index: 10 !important;
}

.sticky-repeat {
  position: sticky !important;
  right: 180px !important;
  background-color: #fff !important;
  z-index: 10 !important;
}

.sticky-status {
  position: sticky !important;
  right: 280px !important;
  background-color: #fff !important;
  z-index: 10 !important;
}

:deep(.my-data-table thead th.sticky-action) {
  position: sticky !important;
  right: 0px !important;
  background-color: #f5f5f5 !important;
  z-index: 15 !important;
}

:deep(.my-data-table thead th.sticky-repeat) {
  position: sticky !important;
  right: 180px !important;
  background-color: #f5f5f5 !important;
  z-index: 15 !important;
}

:deep(.my-data-table thead th.sticky-status) {
  position: sticky !important;
  right: 280px !important;
  background-color: #f5f5f5 !important;
  z-index: 15 !important;
}

:deep(.my-data-table .v-table__wrapper) {
  overflow-x: auto !important;
}

:deep(.my-data-table table) {
  min-width: 1500px;
}

:deep(.v-table .v-table__wrapper > table > tbody > tr > td.sticky-action),
:deep(.v-table .v-table__wrapper > table > thead > tr > th.sticky-action) {
  position: sticky !important;
  right: 0px !important;
  z-index: 10 !important;
}

:deep(.v-table .v-table__wrapper > table > tbody > tr > td.sticky-repeat),
:deep(.v-table .v-table__wrapper > table > thead > tr > th.sticky-repeat) {
  position: sticky !important;
  right: 180px !important;
  z-index: 10 !important;
}

:deep(.v-table .v-table__wrapper > table > tbody > tr > td.sticky-status),
:deep(.v-table .v-table__wrapper > table > thead > tr > th.sticky-status) {
  position: sticky !important;
  right: 280px !important;
  z-index: 10 !important;
}

.data-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: center;
  align-items: center;
  max-width: 200px;
  margin: 0 auto;
}

.data-chip {
  font-size: 12px !important;
  color: #666 !important;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 4px 16px;
  gap: 8px;
  width: 136px;
  height: 32px;
  background-color: #f2f2f2 !important;
  border-radius: 100px;
}

:deep(.data-chip .v-chip__content) {
  font-size: 11px;
  font-weight: 500;
  padding: 0 8px;
}

:deep(.v-chip.data-chip) {
  background-color: #f2f2f2 !important;
  color: black !important;
}

.icon-active {
  color: #4caf50 !important;
  opacity: 1;
}

.icon-inactive {
  color: #e0e0e0 !important;
  opacity: 0.5;
}

:deep(.v-icon.icon-active) {
  color: #4caf50 !important;
}

:deep(.v-icon.icon-inactive) {
  color: #bdbdbd !important;
}

.v-tab {
  text-transform: none !important;
}

.v-chip {
  font-weight: 500;
}

.text-h5 {
  font-weight: 500;
}

.v-card-title {
  font-weight: 500;
}

.v-btn {
  text-transform: none !important;
}

.v-alert {
  border-radius: 8px;
}

.v-text-field, .v-textarea, .v-select {
  margin-bottom: 8px;
}

.v-container {
  padding: 16px;
}

.v-tabs-window {
  background-color: transparent;
}

.v-tabs-window-item {
  padding: 0;
}

/* Language Checkbox Card Styles */
.language-checkbox-item {
  height: 100%;
}

.language-card {
  height: 100%;
  transition: all 0.2s ease-in-out;
  border: 2px solid transparent;
  cursor: pointer;
}

.language-card:hover {
  border-color: rgba(25, 118, 210, 0.3);
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.15);
  transform: translateY(-2px);
}

.language-card--active {
  border-color: rgb(var(--v-theme-primary));
  background-color: rgba(25, 118, 210, 0.05);
}

.language-card--active:hover {
  border-color: rgb(var(--v-theme-primary));
  background-color: rgba(25, 118, 210, 0.08);
}

/* Checkbox styling */
.checkbox-custom :deep(.v-selection-control__input) {
  margin-right: 8px;
}

.checkbox-custom :deep(.v-ripple__container) {
  transform: scale(0.8);
}

/* Make checkbox larger and more prominent */
.checkbox-custom :deep(.v-icon) {
  font-size: 24px !important;
}

/* Text styling for language items */
.language-card .v-card-text {
  position: relative;
}

.language-card .text-body-1 {
  line-height: 1.2;
}

.language-card .text-caption {
  opacity: 0.7;
  line-height: 1.2;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .language-card .v-card-text {
    padding: 12px !important;
  }
  
  .checkbox-custom :deep(.v-icon) {
    font-size: 20px !important;
  }
}

/* Loading and empty states */
.text-center .v-progress-circular {
  margin: 0 auto;
}

/* Menu positioning */
.v-overlay__content {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Color utilities for Vuetify 3 */
.text-grey {
  color: rgb(var(--v-theme-on-surface-variant)) !important;
}

.text-red {
  color: rgb(var(--v-theme-error)) !important;
}

/* Action buttons styling */
.action-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.action-btn {
  width: 32px;
  height: 32px;
  min-width: 32px;
}

.action-btn.edit-btn {
  color: #1976d2 !important;
}

.action-btn.save-btn {
  color: #4caf50 !important;
}

.action-btn.cancel-btn {
  color: #f44336 !important;
}

.action-btn.delete-btn {
  color: #f44336 !important;
}

.action-btn:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

/* Add Language Dialog Styles */
.add-language-dialog {
  border-radius: 16px !important;
}

.dialog-header {
  padding: 20px 16px !important;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dialog-header-add {
  background: #eeeff3;
}

.header-main {
  display: flex;
  align-items: center;
}

.header-icon-container {
  width: 48px;
  height: 48px;
  margin-right: 12px;
}

.icon-background {
  padding: 8px;
  gap: 10px;
  width: 46px;
  height: 46px;
  background: #dee7f3;
  backdrop-filter: blur(2px);
  border-radius: 100px;
  box-shadow: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-text-content {
  flex: 1;
}

.dialog-title {
  font-size: 1.25rem !important;
  color: #1f1f1f;
  margin: 0;
}

.dialog-subtitle {
  color: #898989;
  margin: 4px 0 0 0;
}

.dialog-content {
  padding: 24px;
  max-height: 70vh;
  overflow-y: auto;
}

.form-section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}

.dialog-actions {
  border-top: 1px solid #e9ecef;
  background-color: #f8f9fa;
  border-radius: 0 0 16px 16px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px 24px;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Dialog responsive */
@media (max-width: 768px) {
  .dialog-actions {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }

  .action-buttons {
    justify-content: center;
    width: 100%;
  }

  .action-btn {
    flex: 1;
    max-width: 120px;
  }
  
  .add-language-dialog {
    margin: 16px;
  }

  .dialog-content {
    padding: 16px;
  }
}
</style>